import React, { ReactElement, useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { T } from "react-translator-component";
import { clearGroupInfo } from "../../../../redux/Reducers/gcGroupReducer";
import axios, { CancelTokenSource } from "axios";
import appServices from "../../../../services/app-services/appServices";
import { getRole, isGroupLeader } from "../../../../util/utils";
import GCBackButton from "../../../../components/GreenCity/GCBackButton";
import IconButtonArrow from "../../../../components/GreenCity/IconButtonArrow";
import GroupIconImage from "../../../../components/GreenCity/GroupIconImage";
import { Box, CircularProgress, InputAdornment, TextField } from "@mui/material";
import LinkIcon from '@mui/icons-material/Link';

function GroupInvite(): ReactElement {
  const [groups, setGroups] = useState<any>();
  const location = useLocation();
  const { link } = location.state || {};
  const navigate = useNavigate();
  const dispatch =useDispatch();
  const { user } = useSelector((state: any) => state.userAuth);

  // Memoize groups to avoid unnecessary re-renders if groups don't change
  const memoizedGroups = useMemo(() => groups, [groups]);

  // Memoize handleSearch to avoid re-creating the function on every render
  const handleGroup = (group: any) => {
    dispatch(clearGroupInfo())
    if (isGroupLeader(group, user)) {
      navigate("/groupleader", {
        state: {
          groupId: group.id,
        },
      });
    } else if (group?.user?.id !== user?.id) {
      navigate("/groupinforeview", {state: {groupId: group.id, invitionlink: true}});
    }
  };

  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const cancelTokenRef = useRef<CancelTokenSource | null>(null);

  // Debounce function to limit the number of API calls
  const debounce = (func: (...args: any[]) => void, delay: number) => {
    let debounceTimer: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => func(...args), delay);
    };
  };

  const handleSearch = useCallback((results: any) => {
    setGroups(results);
  }, []);

  // Function to fetch search results
  const fetchSearchResults = async (term: string) => {
    if (term) {
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel("Operation canceled due to new request.");
      }
      cancelTokenRef.current = axios.CancelToken.source();

      try {
        setLoading(true);
        const id=term.split('/groupinvitation?link=')[1];

        if(!id)return;

        const posts = await appServices.searchById(id, {
          cancelToken: cancelTokenRef.current.token,
        });
        handleSearch(posts);
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error("Error fetching search results:", error);
          handleSearch([]);
        }
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(false);
      handleSearch([]);
    }
  };
  // eslint-disable-next-line
  const debouncedFetchSearchResults = useCallback(
    debounce(fetchSearchResults, 800),
    []
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const term = event.target.value;
    setSearchTerm(term);
    debouncedFetchSearchResults(term);
  };

  useEffect(()=>{
    if (link) {
        setSearchTerm(link);
        debouncedFetchSearchResults(link);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[link])

  return (
    <div style={{ paddingTop: 100 }}>
      <GCBackButton onClick={() => navigate(-1)} />

      <div style={{ marginLeft: 10, marginRight: 10 }}>
        <TextField
          variant="outlined"
          fullWidth
          placeholder={`${T("group_invite_placeholder")}`}
          value={searchTerm}
          onChange={handleChange}
          focused={true}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LinkIcon/>
              </InputAdornment>
            ),
            endAdornment: loading ? (
              <InputAdornment position="end">
                <CircularProgress size={24} />
              </InputAdornment>
            ) : null,
          }}
        />
      </div>

      <br />

      <Box display="flex" flexDirection="column">
        {memoizedGroups?.member_groups?.map((group: any, index: number) => (
          <IconButtonArrow
            key={index} // Use a unique key in a real app
            icon={
              <GroupIconImage
                groupId={group?.id}
                alt=""
                style={{ borderRadius: 13, height: 26, width: 26 }}
              />
            }
            title={group?.name || ""} // Title from the group
            role={
              isGroupLeader(group, user) ?
                getRole("leader") :
                  group?.status === "invited" ?
                    `${T('invited')}` :
                      group?.status==='unpaid_approved' ?
                        `${T('payment_pending')}` :
                          getRole(group?.role)
            }
            onClick={() => handleGroup(group)}
          />
        ))}
      </Box>
    </div>
  );
}

export default GroupInvite;
